/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  IssuerFieldMismatch,
  IssuerFieldMismatch$inboundSchema,
} from "./issuerfieldmismatch.js";
import {
  TrustedUserSessionIssuerReference,
  TrustedUserSessionIssuerReference$inboundSchema,
} from "./trustedusersessionissuerreference.js";

/**
 * Authoritative impact summary for migrating a remote_session_issuer's clients onto another issuer: how many clients move, which MCP servers are affected, and every blocker that would make the migration fail.
 */
export type OrganizationIssuerMigratePreflight = {
  /**
   * TRUE when the migration would succeed: no active identity-chaining bindings, endpoint mismatches, conflicting MCP-server bindings, or user-session issuers that trust the source.
   */
  canMigrate: boolean;
  /**
   * Number of non-deleted remote_session_clients that would be re-pointed from the source issuer to the target issuer.
   */
  clientCount: number;
  /**
   * Display names of MCP servers where both the source and the target issuer already have a client bound. Non-empty blocks the migration; detach one client per listed server and retry.
   */
  conflictingMcpServerNames: Array<string>;
  /**
   * Number of active identity-chaining bindings on the source. Non-zero blocks migration; explicitly unlink these bindings before migration, then prepare new bindings for the target.
   */
  emaBindingCount: number;
  /**
   * The authorization-server metadata fields (issuer, token_endpoint, authorization_endpoint) that differ between source and target, with both sides' values. Non-empty blocks the migration.
   */
  endpointMismatches: Array<IssuerFieldMismatch>;
  /**
   * Display names of MCP servers attached to the source issuer's clients.
   */
  mcpServerNames: Array<string>;
  /**
   * User-session issuers that trust the source and block migration until explicitly unlinked or re-linked.
   */
  trustedUserSessionIssuers: Array<TrustedUserSessionIssuerReference>;
  /**
   * Non-blocking divergences (oidc, passthrough, scopes_supported), with both sides' values. The target issuer's values become authoritative for the migrated clients.
   */
  warnings: Array<IssuerFieldMismatch>;
};

/** @internal */
export const OrganizationIssuerMigratePreflight$inboundSchema: z.ZodMiniType<
  OrganizationIssuerMigratePreflight,
  unknown
> = z.pipe(
  z.object({
    can_migrate: z.boolean(),
    client_count: z.int(),
    conflicting_mcp_server_names: z.array(z.string()),
    ema_binding_count: z.int(),
    endpoint_mismatches: z.array(IssuerFieldMismatch$inboundSchema),
    mcp_server_names: z.array(z.string()),
    trusted_user_session_issuers: z.array(
      TrustedUserSessionIssuerReference$inboundSchema,
    ),
    warnings: z.array(IssuerFieldMismatch$inboundSchema),
  }),
  z.transform((v) => {
    return remap$(v, {
      "can_migrate": "canMigrate",
      "client_count": "clientCount",
      "conflicting_mcp_server_names": "conflictingMcpServerNames",
      "ema_binding_count": "emaBindingCount",
      "endpoint_mismatches": "endpointMismatches",
      "mcp_server_names": "mcpServerNames",
      "trusted_user_session_issuers": "trustedUserSessionIssuers",
    });
  }),
);

export function organizationIssuerMigratePreflightFromJSON(
  jsonString: string,
): SafeParseResult<OrganizationIssuerMigratePreflight, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) =>
      OrganizationIssuerMigratePreflight$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'OrganizationIssuerMigratePreflight' from JSON`,
  );
}
